/**
 * Daily Rental Report: contracts opened / closed per branch for a day or a date range, the
 * employee who opened each one, the cars summary (rented by category, maintenance, ready …),
 * Excel export, column copy, and a search over Business Bay contracts with bulk delete
 * (the tools that used to live on Daily Booking Management).
 *
 * Sources
 *   Business Bay + Invygo  `contracts` collection (Refresh reads the database, not the 24h cache)
 *   Airport T1 / T2        `airport_contracts`, queried exactly for each day shown
 *   Cars                   the fleet (Ejar status) + open contracts, same rule as Fleet Manager
 * "Today" is the Dubai date.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { contractsService, airportService, fleetService } from "../../services/firestore";
import { adminService } from "../../services/adminService";
import { jsonCacheService } from "../../services/jsonCacheService";
import { formatPlateNumber } from "../../utils/plateFormatter";
import { useAuth } from "../../contexts/AuthContext";
import { usePermissions } from "../../hooks/usePermissions";
import useFleetUsage from "../../hooks/useFleetUsage";
import { bookingsService, fleetSnapshotService, bookingState, bookingClass, bookingModel, bookingPayment, bookingTime, bookingConfirmMessage } from "../../services/bookingsService";
import { contractCategory } from "../ContractsManagement/contracts.logic";
import { INVYGO_NO_MONEY_NOTE } from "../ContractsManagement/utils/contractRows";
import { LEASING, periodKind, businessBayKindOf } from "../../services/contractRules";
import { PERMISSIONS } from "../../constants/permissions";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Badge from "../../ui/Badge";
import Icon from "../../ui/Icon";
import { ICONS } from "../../ui/icon-map";
import EmptyState from "../../ui/EmptyState";
import Tabs from "../../ui/Tabs/Tabs";
import Stat, { StatGrid } from "../../ui/Stat/Stat";
import LoadingSpinner from "../../components/LoadingSpinner";
import { Field, Input, Select, FormGrid } from "../../ui/Field/Field";
import Dialog from "../../ui/Dialog/Dialog";
import { finesService, errorMessage } from "../../services/finesService";
import { toast, confirmDialog } from "../../ui/feedback";
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "../../ui/Table";
import { CellId, CellText, CellPlate, CellNumber, CellTag, categoryTone } from "../../ui/DataTable/cells";
import CustomerProfileModal from "../../components/CustomerProfileModal";
import AvailabilityDialog from "./AvailabilityDialog";
import logger from "../../utils/logger";
import { formatMoney } from "../../constants/finance";
import { inFleet, isGone, isRtaRegistered } from "../../services/fleetStatus";
import { phoneDigits, formatPhone, whatsappUrl } from "../../utils/phoneUtils";
import { formatContractDateTime } from "../../utils/contractDateUtils";
import { contractDateSheetCell, fitContractDateColumns } from "../../utils/excelDates";
import { USE_CONTRACTS_V2 } from "../../services/contractsV2";

const g = (row, ...keys) => { for (const k of keys) { const v = row[k]; if (v !== undefined && v !== null && String(v).trim() !== "") return v; } return ""; };
const statusOf = (row) => String(g(row, "Status", "status")).trim();
const isClosed = (row) => /deliver|close/.test(statusOf(row).toLowerCase());
// Did this contract hold its car on `day` (YYYY-MM-DD)? Picked up by then and not yet due back - or still open: a
// contract past its return date keeps the car until it is closed (18 Oct 2026: 59 Business Bay cars on overdue
// monthly contracts showed as "Other" in the night's cars summary).
// The category of a past night's rented car from the contract the night recorded (nightContract / nightContractBranch):
// that contract's own type when the page has it, else its branch - an airport contract is "Airport", Invygo's "Invygo";
// null when the night recorded none (older nights) or it cannot tell.
export const nightCategory = (car, contractsByNo) => {
    if (!car?.nightContract) return null;
    const own = contractsByNo.get(String(car.nightContract).toUpperCase());
    if (own) return contractCategory(own) || "Monthly";
    if (car.nightContractBranch === "T1" || car.nightContractBranch === "T2") return "Airport";
    if (car.nightContractBranch === "INV") return "Invygo";
    return null;
};

// A cancelled contract never held a car.
export const contractCoversDay = (c, day) =>
    Boolean(c && c.pickIso && c.pickIso <= day && !/cancel/i.test(statusOf(c.raw))
        && (!c.dropIso || c.dropIso >= day || !isClosed(c.raw)));
const parseDate = (s) => { if (!s) return null; const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(String(s).trim()); if (m) return new Date(+m[3], +m[2] - 1, +m[1]); const d = new Date(s); return isNaN(d.getTime()) ? null : d; };
const pad = (n) => String(n).padStart(2, "0");
const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const isoDay = (s) => { const d = parseDate(s); return d ? isoOf(d) : ""; };
const timeOf = (s) => { const t = /(\d{1,2}):(\d{2})/.exec(String(s || "")); return t ? `${pad(+t[1])}:${t[2]}` : ""; };
// a contract's pick-up / drop-off as shown: "dd/mm/yyyy HH:MM" (the date alone when it has no time); text
// that cannot be read as it is (owner, 27 Sep 2026)
const shownWhen = (v) => formatContractDateTime(v) || v || "";
const toDDMMYYYY = (iso) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || ""); return m ? `${m[3]}/${m[2]}/${m[1]}` : ""; };
// A day is 24 hours, Dubai time, 00:00:00 -> 23:59:59 (owner, 1 Oct 2026: "اعتبار اليوم 24 ساعه من 12 صباحا حتى 11:59:59
// مساءا"); today is "Live" - from 00:00:00 to now ("وانهارده خلى اسمه لايف"). Every count of the page takes a contract or a
// booking by the Dubai date of its time ("dd/mm/yyyy HH:MM", Dubai), so these are the windows it counts in.
export const dayWindowText = (day, today) => (day === today
    ? `Live · ${toDDMMYYYY(day)} 00:00:00 → now`
    : `${toDDMMYYYY(day)} 00:00:00 → 23:59:59`);
export const rangeWindowText = (from, to, today) => (from && to
    ? `${toDDMMYYYY(from)} 00:00:00 → ${toDDMMYYYY(to)} ${to === today ? "now (Live)" : "23:59:59"}`
    : "");
const isAirportBranch = (b) => b === "Airport Terminal 1" || b === "Airport Terminal 2";

/** Today in Dubai (the browser clock may be on UTC or another zone). */
const dubaiToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Dubai", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
// the day before a yyyy-mm-dd day (calendar days: no clock, no daylight saving)
const dayBefore = (d) => new Date(Date.parse(`${d}T00:00:00Z`) - 86400000).toISOString().slice(0, 10);
const daysBetween = (from, to) => {
    const out = [];
    const d = new Date(`${from}T00:00:00`);
    const end = new Date(`${to}T00:00:00`);
    while (d <= end && out.length < 62) { out.push(isoOf(d)); d.setDate(d.getDate() + 1); }
    return out;
};
const MAX_RANGE_DAYS = 31;

const branchOf = (row) => {
    const b = String(g(row, "Pick-up Branch", "pickup_branch")).toLowerCase();
    if (b.includes("invygo")) return "Invygo";
    if (b.includes("terminal 2")) return "Airport Terminal 2";
    if (b.includes("terminal 1")) return "Airport Terminal 1";
    return "Business Bay";
};

// Rental type: contract no starting "C" = Leasing (the one name of the app); else the pick-up→drop-off
// period by the app's one rule - monthly from 30 days (contractRules; it was 25 here - DUP-01 / CON-10).
const rentalType = (row) => {
    const cn = String(g(row, "Contract No.", "contract_no", "id")).trim().toUpperCase();
    if (cn.startsWith("C")) return LEASING;
    const bayKind = businessBayKindOf(row, cn);       // Business Bay: the engine's own (owner, 14 Oct 2026)
    if (bayKind) return bayKind;
    const s = parseDate(g(row, "Pick-up Date", "pickup_date"));
    const e = parseDate(g(row, "Drop-off Date", "dropoff_date"));
    return periodKind(s, e) || "Daily";
};

const tag = (row) => ({
    id: row.id || "",
    branch: branchOf(row),
    type: rentalType(row),
    closed: isClosed(row),
    status: statusOf(row),
    pickIso: isoDay(g(row, "Pick-up Date", "pickup_date")),
    dropIso: isoDay(g(row, "Drop-off Date", "dropoff_date")),
    pickup: g(row, "Pick-up Date", "pickup_date"),
    dropoff: g(row, "Drop-off Date", "dropoff_date"),
    pickTime: timeOf(g(row, "Pick-up Date", "pickup_date")),
    dropTime: timeOf(g(row, "Drop-off Date", "dropoff_date")),
    contract: String(g(row, "Contract No.", "contract_no", "id")).toUpperCase(),
    booking: g(row, "Booking Number", "booking_number"),
    customer: g(row, "Customer", "customer"),
    phone: g(row, "Phone Number", "phone"),
    plate: formatPlateNumber(g(row, "Plate No.", "plate_no", "plate")),
    model: g(row, "Model", "model"),
    openedBy: g(row, "Created By", "created_by", "Opened By", "Opened by", "Staff", "User"),
});

const BRANCHES = ["Invygo", "Business Bay", "Airport Terminal 1", "Airport Terminal 2"];
const TYPES = ["Daily", "Monthly", LEASING];

/* ---------- One branch table: header click selects columns, Ctrl+C / Copy copies them ---------- */
const COLUMNS = [
    { key: "contract", label: "Contract No." },
    { key: "customer", label: "Customer" },
    { key: "plate", label: "Plate No." },
    { key: "model", label: "Model" },
    { key: "when", label: "Date / time" },
    { key: "type", label: "Type" },
    { key: "openedBy", label: "Opened by" },
];
const cellValue = (r, key, kind) => (key === "when" ? shownWhen(kind === "closed" ? r.dropoff : r.pickup) : r[key] || "");

const copyText = async (text) => {
    try {
        if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); return true; }
        const ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); return true;
    } catch (e) { return false; }
};

const ContractsTable = ({ rows, kind, onCustomer, showDate }) => {
    const [picked, setPicked] = useState([]);
    const toggle = (key) => setPicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
    const copy = async () => {
        const cols = COLUMNS.filter((c) => picked.length === 0 || picked.includes(c.key));
        const text = [cols.map((c) => c.label).join("\t"), ...rows.map((r) => cols.map((c) => cellValue(r, c.key, kind)).join("\t"))].join("\n");
        if (await copyText(text)) toast.success(`Copied ${rows.length} rows (${cols.length} columns)`);
    };
    return (
        <>
            <TableContainer>
                <Table
                    tabIndex={0}
                    onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "c" && picked.length) { e.preventDefault(); copy(); } }}
                >
                    <TableHead>
                        <TableRow>
                            <TableHeader>#</TableHeader>
                            {COLUMNS.map((c) => (
                                <TableHeader
                                    key={c.key}
                                    onClick={() => toggle(c.key)}
                                    className={`cursor-pointer select-none ${picked.includes(c.key) ? "is-picked" : ""}`}
                                    aria-pressed={picked.includes(c.key)}
                                    title="Click to select this column for copy"
                                >
                                    {c.key === "when" ? (kind === "closed" ? "Closed" : "Opened") + (showDate ? " at" : " time") : c.label}
                                </TableHeader>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow><TableCell colSpan={COLUMNS.length + 1} className="py-5 text-gray-500">None</TableCell></TableRow>
                        ) : rows.map((r, i) => (
                            <TableRow key={r.contract + i}>
                                <TableCell><CellNumber value={i + 1} /></TableCell>
                                <TableCell><CellId>{r.contract}</CellId></TableCell>
                                <TableCell>
                                    {r.customer ? <button type="button" className="hover:underline" onClick={() => onCustomer(r)} title="View customer history"><CellText>{r.customer}</CellText></button> : <CellText />}
                                </TableCell>
                                <TableCell><CellPlate>{r.plate}</CellPlate></TableCell>
                                <TableCell><CellText>{r.model}</CellText></TableCell>
                                <TableCell>
                                    <span className="ds-cell-date">
                                        {showDate ? shownWhen(kind === "closed" ? r.dropoff : r.pickup) || "—" : (kind === "closed" ? r.dropTime : r.pickTime) || "—"}
                                    </span>
                                </TableCell>
                                <TableCell><CellTag>{r.type}</CellTag></TableCell>
                                <TableCell><CellText>{r.openedBy || "—"}</CellText></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {rows.length > 0 && (
                <div className="flex items-center justify-between gap-2 mt-2 text-xs text-gray-500">
                    <span>{picked.length ? `${picked.length} column(s) selected · Ctrl+C or Copy` : "Click headers to pick columns to copy"}</span>
                    <Button size="xs" variant="ghost" leadingIcon={<Icon glyph={ICONS.copy} size="xs" />} onClick={copy}>
                        {picked.length ? "Copy selected" : "Copy table"}
                    </Button>
                </div>
            )}
        </>
    );
};

/* ---------- Cars summary (what the fleet is doing), live for today ---------- */
const USAGE_ORDER = ["Monthly", "Daily", "Invygo", "Leasing", "Airport", "Rented"];
// Ejar car statuses: colour + a plain explanation (tooltip)
const STATUS_INFO = [
    { test: /preventive/i, tone: "warning", hint: "Scheduled service (oil, filters…)" },
    { test: /maint|repair|accident/i, tone: "warning", hint: "In the workshop" },
    { test: /ready to sell|sell/i, tone: "info", hint: "Taken out of rental, waiting to be sold" },
    { test: /ready|available/i, tone: "success", hint: "Ready to rent" },
    { test: /replace/i, tone: "accent", hint: "Out as a replacement car - usually for a customer whose car is in maintenance or an accident; no rental contract of its own" },
    { test: /stolen/i, tone: "danger", hint: "Reported stolen" },
    { test: /sold/i, tone: "neutral", hint: "Sold but still in the RTA list" },
];
const statusInfo = (s) => STATUS_INFO.find((i) => i.test.test(s)) || { tone: "neutral", hint: s };

// Booking cars are parked in a "Maintenance" status at the airport branches: that is a car held for a
// booking. At Business Bay and Invygo it is real maintenance (owner, 29 Oct 2026: "اى سيارة فى فرع بيزنيس باى
// او انفيجو فى المينتينانس تبقى صيانة حقيقية") - the engine's one rule, api/overview.py HELD_BRANCHES.
// Cars on their way out of the fleet (for sale, sold, stolen) are not part of the report: the fleet's one rule
const inService = (car) => !isGone(car?.status);
const HELD = "Held for bookings";
const MAINT_RE = /maint|repair|service|accident/i;
const isBookingBranch = (branch) => /airport|terminal/i.test(String(branch || ""));
export const isHeld = (car) => MAINT_RE.test(String(car?.status || "")) && isBookingBranch(car?.branch);
const idleKeyOf = (car) => (isHeld(car) ? HELD : String(car?.status || "Unknown").trim() || "Unknown");
STATUS_INFO.unshift({ test: /^held for bookings$/i, tone: "info",
    hint: "In Maintenance status at an airport branch: kept aside for a booking (real maintenance is at Business Bay / Invygo)" });

const Chip = ({ tone, active, onClick, title, children }) => (
    <button
        type="button"
        className={`yelo-badge yelo-badge--${tone} ds-chip ${active ? "is-active" : ""}`}
        aria-pressed={active}
        title={title}
        onClick={onClick}
    >
        {children}
    </button>
);

const CarsSummary = ({ cars = [], usageOf = () => null, ready, title, subtitle, empty }) => {
    const [picked, setPicked] = useState(null); // { kind: 'rented' | 'status', key }
    const summary = useMemo(() => {
        const rented = {}; const idle = {};
        let rentedTotal = 0;
        cars.forEach((c) => {
            const u = usageOf(c);
            if (u) { rented[u] = (rented[u] || 0) + 1; rentedTotal += 1; }
            else { const s = idleKeyOf(c); idle[s] = (idle[s] || 0) + 1; }
        });
        const held = idle[HELD] || 0;
        const maintenance = Object.entries(idle).filter(([s]) => s !== HELD && MAINT_RE.test(s)).reduce((a, [, n]) => a + n, 0);
        const available = Object.entries(idle).filter(([s]) => /ready|available/i.test(s) && !/sell/i.test(s)).reduce((a, [, n]) => a + n, 0);
        return { rented, idle, rentedTotal, maintenance, held, available, total: cars.length };
    }, [cars, usageOf]);

    const pickedCars = useMemo(() => {
        if (!picked) return [];
        return cars.filter((c) => {
            const u = usageOf(c);
            return picked.kind === "rented" ? u === picked.key : !u && idleKeyOf(c) === picked.key;
        });
    }, [picked, cars, usageOf]);
    const toggle = (kind, key) => setPicked((p) => (p && p.kind === kind && p.key === key ? null : { kind, key }));

    if (empty) {
        return (
            <Card className="mb-6" title={title || "Cars"} subtitle="What the fleet was doing on this day">
                <EmptyState icon={ICONS.fleet} title={empty.title} description={empty.description} />
            </Card>
        );
    }
    const pct = (n) => (summary.total ? `${Math.round((n / summary.total) * 100)}% of fleet` : "");
    return (
        <Card className="mb-6" title={title} subtitle={`${summary.total} cars in the fleet · ${subtitle} · click a group to see its cars`}>
            <StatGrid min={160}>
                <Stat label="Rented" value={ready ? summary.rentedTotal : "…"} icon={ICONS.car} meta={pct(summary.rentedTotal)} />
                <Stat label="In maintenance" value={summary.maintenance} icon={ICONS.maintenance} tone={summary.maintenance ? "warning" : undefined} meta={`Business Bay / Invygo · ${pct(summary.maintenance)}`} />
                <Stat label="Held for bookings" value={summary.held} icon={ICONS.calendar} meta="Maintenance status at the airport" />
                <Stat label="Ready" value={summary.available} icon={ICONS.check} tone="success" meta={pct(summary.available)} />
                <Stat label="Utilisation" value={summary.total ? `${Math.round((summary.rentedTotal / summary.total) * 100)}%` : "—"} icon={ICONS.kpi} meta="rented ÷ fleet" />
            </StatGrid>
            <div className="grid gap-4 md:grid-cols-2 mt-4">
                <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Rented by category</h4>
                    <div className="flex flex-wrap gap-2">
                        {USAGE_ORDER.filter((u) => summary.rented[u]).map((u) => (
                            <Chip
                                key={u}
                                tone={u === "Rented" ? "neutral" : categoryTone(u)}
                                active={picked && picked.kind === "rented" && picked.key === u}
                                onClick={() => toggle("rented", u)}
                                title={`Show the ${summary.rented[u]} cars on ${u === "Rented" ? "other" : u} rentals`}
                            >
                                {u === "Rented" ? "Other" : u} <strong className="tabular-nums">{summary.rented[u]}</strong>
                            </Chip>
                        ))}
                    </div>
                </div>
                <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Not rented, by status</h4>
                    <div className="flex flex-wrap gap-2">
                        {Object.entries(summary.idle).sort((a, b) => b[1] - a[1]).map(([s, n]) => {
                            const info = statusInfo(s);
                            return (
                                <Chip
                                    key={s}
                                    tone={info.tone}
                                    active={picked && picked.kind === "status" && picked.key === s}
                                    onClick={() => toggle("status", s)}
                                    title={`${info.hint} — show the ${n} cars`}
                                >
                                    {info.label || s} <strong className="tabular-nums">{n}</strong>
                                </Chip>
                            );
                        })}
                    </div>
                </div>
            </div>
            {picked && (
                <div className="mt-4">
                    <div className="flex items-center justify-between gap-2 mb-2">
                        <h4 className="text-sm font-semibold text-gray-900 m-0">
                            {picked.kind === "rented" ? `On ${picked.key === "Rented" ? "other" : picked.key} rentals` : (statusInfo(picked.key).label || picked.key)} · {pickedCars.length} cars
                        </h4>
                        <Button size="xs" variant="ghost" onClick={() => setPicked(null)}>Close</Button>
                    </div>
                    {picked.kind === "status" && <p className="text-xs text-gray-500 mb-2">{statusInfo(picked.key).hint}</p>}
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>#</TableHeader><TableHeader>Plate No.</TableHeader><TableHeader>Model</TableHeader><TableHeader>Class</TableHeader><TableHeader>Ejar status</TableHeader><TableHeader>Branch</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pickedCars.map((c, i) => (
                                    <TableRow key={`${c.plate_number}-${i}`}>
                                        <TableCell><CellNumber value={i + 1} /></TableCell>
                                        <TableCell><CellPlate>{c.plate_number}</CellPlate></TableCell>
                                        <TableCell><CellText>{[c.make, c.model].filter(Boolean).join(" ")}</CellText></TableCell>
                                        <TableCell><CellText>{c.category}</CellText></TableCell>
                                        <TableCell><CellText>{c.status}</CellText></TableCell>
                                        <TableCell><CellText>{c.branch}</CellText></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            )}
        </Card>
    );
};

const exportRow = (r) => ({
    "Contract No.": r.contract,
    "Booking Number": r.booking || "",
    Customer: r.customer || "",
    "Pick-up Branch": r.branch,
    "Plate No.": r.plate || "",
    Model: r.model || "",
    // real Excel dates shown dd/mm/yyyy hh:mm: they sort and filter by date in Excel
    "Pick-up Date": contractDateSheetCell(r.pickup),
    "Drop-off Date": contractDateSheetCell(r.dropoff),
    "Phone Number": formatPhone(phoneDigits(r.phone)),
    Type: r.type,
    Status: r.status || "",
    "Opened by": r.openedBy || "",
});

/* ---------- Search Business Bay contracts, export what was found ---------- */
// No delete here (CON-06, owner 26 Sep 2026): Ejar contracts are never deleted, and a contract removed from
// Yelo while it is still on Ejar only comes back - Yelo must not bulk-delete contracts.
const SearchContracts = ({ rows, canExport, onCustomer }) => {
    const [q, setQ] = useState("");
    const term = q.trim().toLowerCase();
    const found = useMemo(() => {
        if (term.length < 2) return [];
        return rows.filter((r) => [r.contract, r.customer, r.plate, r.phone, r.booking, r.model, r.openedBy].some((v) => String(v || "").toLowerCase().includes(term)));
    }, [rows, term]);

    const exportFound = async () => {
        const XLSX = await import("xlsx");
        const ws = fitContractDateColumns(XLSX.utils.json_to_sheet(found.map(exportRow)));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Search Results");
        XLSX.writeFile(wb, "Contracts_Search_Results.xlsx");
    };
    // At the top of the page: the search bar only; the results card opens under it while searching
    return (
        <>
            <div className="ds-toolbar mb-4">
                <div className="ds-search" style={{ flex: "1 1 420px", maxWidth: 640 }}>
                    <Icon glyph={ICONS.search} size="sm" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search all Business Bay / Invygo contracts: contract, customer, plate, phone, booking, employee…" aria-label="Search contracts" />
                </div>
                {q && <Button size="sm" variant="ghost" onClick={() => setQ("")}>Clear</Button>}
                {term.length >= 2 && <span className="text-sm text-gray-600 tabular-nums">{found.length} found</span>}
                <span className="ds-toolbar__spacer" />
                {canExport && found.length > 0 && <Button size="sm" leadingIcon={<Icon glyph={ICONS.export} size="sm" />} onClick={exportFound}>Export results</Button>}
            </div>
            {term.length >= 2 && (
                <Card className="mb-6" title={`Search results for "${q.trim()}"`} subtitle="Business Bay and Invygo contracts, all dates">
                    <ContractsTable rows={found.slice(0, 300)} kind="opened" onCustomer={onCustomer} showDate />
                    {found.length > 300 && <p className="text-xs text-gray-500 mt-2">Showing the first 300 — refine the search or export to see all.</p>}
                </Card>
            )}
        </>
    );
};

/* ---------- Bookings (Ejar, synced hourly by the engine) ---------- */
const BOOKING_STATES = [
    { key: "open", label: "Open", tone: "info" },
    { key: "pending", label: "Awaiting payment", tone: "warning" },
    { key: "contract", label: "Became contract", tone: "success" },
    { key: "noshow", label: "No-show", tone: "danger" },
    { key: "cancelled", label: "Cancelled", tone: "neutral" },
];
const PAYMENT_VIEW = {
    paid: { label: "Paid", tone: "success" },
    due: { label: "Not paid", tone: "danger" },
    broker: { label: "Broker", tone: "info", hint: "A broker booking: the broker pays the balance by invoice" },
    awaiting: { label: "Awaiting payment", tone: "warning" },
    unknown: { label: "—", tone: "neutral" },
};
// the one money format (DUP-27): 2 decimals, en-AE grouping, AED first, empty -> — (the text around it says AED)
const money = (n) => formatMoney(n, { currency: false });
const shortDay = (iso, today) => {
    const d = new Date(`${iso}T00:00:00`);
    const t = new Date(`${today}T00:00:00`);
    const diff = Math.round((d - t) / 86400000);
    const name = d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });
    if (diff === 0) return `Live · ${name}`;
    if (diff === 1) return `Tomorrow · ${name}`;
    if (diff === -1) return `Yesterday · ${name}`;
    return name;
};
const addDays = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return isoOf(d); };
// the anchor of a branch block in the contracts part (the summary table jumps to it)
const jumpId = (branch, kind) => `rental-${String(branch).toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${kind}`;
// Compact cells for the bookings list (no horizontal scroll)
// Date on one line, time under it (keeps the bookings list narrow)
const WhenCell = ({ text, title }) => {
    const m = /^(\d{2}\/\d{2})\/\d{4}\s*(\d{2}:\d{2})?/.exec(String(text || ""));
    if (!m) return <span className="ds-cell-empty">—</span>;
    return (
        <span className="inline-flex flex-col items-center leading-tight" title={title || text}>
            <span className="ds-cell-date">{m[1]}</span>
            {m[2] && <span className="ds-cell-muted">{m[2]}</span>}
        </span>
    );
};
const SHORT_BRANCH = { "Airport Terminal 1": "Airport T1", "Airport Terminal 2": "Airport T2", "Business Bay": "Business Bay", Invygo: "BB · Invygo" };
const shortBranch = (branch) => SHORT_BRANCH[branchOf({ "Pick-up Branch": branch })] || branch || "—";

const PaymentCell = ({ b }) => {
    const pay = bookingPayment(b);
    const view = PAYMENT_VIEW[pay.state];
    if (pay.state === "unknown") return <span className="ds-cell-empty">—</span>;
    // an Invygo booking is paid to Invygo, never Yelo: '—', not 'Paid' (owner, 28 Sep 2026)
    if (pay.state === "invygo") return <span className="ds-cell-empty" title={INVYGO_NO_MONEY_NOTE}>—</span>;
    return (
        <span className="inline-flex flex-col items-center gap-0.5">
            <span title={view.hint}><CellTag tone={view.tone}>{view.label}</CellTag></span>
            {/* paid: what was paid online (Ejar's balancePayment, before VAT); due: a bot booking's amount still to pay */}
            {pay.amount > 0 && <span className={`ds-cell-muted ${pay.state === "due" ? "ds-cell--danger" : ""}`}>AED {money(pay.amount)} {pay.state === "paid" ? "online" : pay.state === "broker" ? "on the invoice" : "due"}</span>}
        </span>
    );
};

// A booking made outside Ejar (WhatsApp / phone / counter): it never goes on Ejar. The engine saves
// it (manual_bookings) and sends the Telegram notice; it then shows here with the Ejar bookings.
const BOOKING_BRANCHES = [["t1", "Airport T1"], ["t2", "Airport T2"], ["inv", "Invygo"], ["bb", "Business Bay"]];
const BOOKING_SOURCES = ["WhatsApp", "Phone", "Walk-in", "Other"];
const AddBookingDialog = ({ open, onClose, onSaved, today }) => {
    const blank = { customer: "", mobile: "", vehicle: "", branch: "t1", pickup: `${addDays(today, 1)}T10:00`, source: "WhatsApp", amount: "", paid: false, notes: "" };
    const [f, setF] = useState(blank);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    useEffect(() => { if (open) { setF(blank); setErr(""); } }, [open]);   // eslint-disable-line react-hooks/exhaustive-deps
    const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
    const save = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        try {
            const doc = await finesService.manualBookingAdd(f);
            onSaved(doc);
        } catch (x) { setErr(errorMessage(x)); }
        setBusy(false);
    };
    return (
        <Dialog open={open} onClose={onClose} size="md" title="Add a booking (not on Ejar)"
            description="A customer on WhatsApp, the phone or at the counter. It shows here with the Ejar bookings and goes to Telegram at once."
            footer={(
                <>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="submit" form="add-booking-form" variant="primary" isLoading={busy}>Save booking</Button>
                </>
            )}>
            <form id="add-booking-form" onSubmit={save} className="flex flex-col gap-4">
                {err && <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
                <FormGrid cols={2}>
                    <Field label="Customer" required><Input value={f.customer} onChange={set("customer")} required autoFocus /></Field>
                    <Field label="Phone" required><Input value={f.mobile} onChange={set("mobile")} placeholder="050 123 4567" required inputMode="tel" /></Field>
                    <Field label="Car (model and year)" required><Input value={f.vehicle} onChange={set("vehicle")} placeholder="Arrizo 5 2025" required /></Field>
                    <Field label="Branch" required>
                        <Select value={f.branch} onChange={set("branch")}>{BOOKING_BRANCHES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select>
                    </Field>
                    <Field label="Pick-up" required><Input type="datetime-local" value={f.pickup} onChange={set("pickup")} required /></Field>
                    <Field label="Came from">
                        <Select value={f.source} onChange={set("source")}>{BOOKING_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}</Select>
                    </Field>
                    <Field label="Price (AED)" optional><Input type="number" min="0" step="0.01" value={f.amount} onChange={set("amount")} /></Field>
                    <Field label="Paid already">
                        <label className="inline-flex items-center gap-2 text-sm h-9"><input type="checkbox" checked={f.paid} onChange={set("paid")} /> The customer paid</label>
                    </Field>
                </FormGrid>
                <Field label="Notes" optional><Input value={f.notes} onChange={set("notes")} placeholder="flight, child seat, delivery…" /></Field>
            </form>
        </Dialog>
    );
};

export const UpcomingBookings = ({ today, fleetCars, reloadKey }) => {
    // adding a booking outside Ejar (it goes to Telegram) and marking one picked up / cancelled: each its own permission
    // (permissions phase 4, 4 Oct 2026)
    const { hasPermission } = usePermissions();
    const canAdd = hasPermission('add_manual_booking');
    const canUpdate = hasPermission('update_manual_booking');
    const [state, setState] = useState({ loading: true, rows: [], meta: null, error: null });
    const [adding, setAdding] = useState(false);
    const [checking, setChecking] = useState(false);                     // "Is a car free?"
    const [tick, setTick] = useState(0);                                  // reload after an add / close
    const closeManual = async (b, status) => {
        if (!(await confirmDialog({
            title: status === "picked" ? `${b.customer} picked the car up?` : `Cancel ${b.customer}'s booking?`,
            message: `${b.vehicle || ""} · ${b.pickup_date || ""} · ${shortBranch(b.pickup_branch)} (outside Ejar, ${b.source || "manual"})`,
            confirmLabel: status === "picked" ? "Yes, picked up" : "Yes, cancel it",
            danger: status === "cancelled",
        }))) return;
        try { await finesService.manualBookingStatus(b.booking_number, status); setTick((t) => t + 1); }
        catch (e) { toast.error(errorMessage(e)); }
    };
    const [picked, setPicked] = useState(today);                          // "new" | "yyyy-mm-dd"
    const [moreDays, setMoreDays] = useState(false);                      // the other days are open
    const chipsRef = useRef(null);
    const [branch, setBranch] = useState(null);                           // null = every branch
    const [only, setOnly] = useState(null);                               // "cancelled": the list shows only those
    useEffect(() => { setOnly(null); }, [picked]);
    const from = useMemo(() => addDays(today, -1), [today]);
    const tomorrow = addDays(today, 1);

    // yesterday .. 60 days ahead: a new booking can be for any date
    useEffect(() => {
        let alive = true;
        setState((s) => ({ ...s, loading: true, error: null }));
        Promise.all([bookingsService.meta(), bookingsService.forDays(from, addDays(today, 60)), bookingsService.manualForDays(from, addDays(today, 60))])
            .then(([meta, rows, manual]) => { if (alive) setState({ loading: false, rows: [...(rows || []), ...(manual || [])], meta, error: null }); })
            .catch((e) => { logger.error("Bookings load failed:", e); if (alive) setState({ loading: false, rows: [], meta: null, error: e }); });
        return () => { alive = false; };
    }, [from, today, reloadKey, tick]);

    const byDay = useMemo(() => {
        const m = {};
        state.rows.forEach((b) => { (m[b.pickup_day] = m[b.pickup_day] || []).push(b); });
        Object.values(m).forEach((l) => l.sort((a, b) => String(a.pickup_date).slice(11).localeCompare(String(b.pickup_date).slice(11))));
        return m;
    }, [state.rows]);
    // New = made today (Dubai), whatever day it is for
    const fresh = useMemo(() => state.rows
        .map((b) => ({ b, at: bookingTime(b.created_at) }))
        .filter((x) => x.at && isoOf(x.at) === today)
        .sort((x, y) => y.at - x.at)
        .map((x) => x.b), [state.rows, today]);

    // only the days that have bookings (cancelled ones do not count)
    const liveCount = (d) => (byDay[d] || []).filter((b) => bookingState(b) !== "cancelled").length;
    const days = useMemo(() => Object.keys(byDay).filter((d) => d >= from && liveCount(d) > 0).sort(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [byDay, from]);
    // Today (live) and Tomorrow always; the other days with bookings behind "+", all in date order
    const extraDays = days.filter((d) => d !== today && d !== tomorrow);
    const hiddenDays = extraDays.filter((d) => d !== picked);
    const shownDays = [...new Set([today, tomorrow, ...extraDays.filter((d) => moreDays || d === picked)])].sort();
    // a click outside the chips folds the other days back behind "+"
    useEffect(() => {
        if (!moreDays) return undefined;
        const onDown = (e) => { if (chipsRef.current && !chipsRef.current.contains(e.target)) setMoreDays(false); };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [moreDays]);
    const isNew = picked === "new";
    const dayList = isNew ? fresh : (byDay[picked] || []);
    const branchOfBooking = (b) => branchOf({ "Pick-up Branch": b.pickup_branch });
    const branchCards = BRANCHES.map((br) => {
        const l = dayList.filter((b) => bookingState(b) !== "cancelled" && branchOfBooking(b) === br);
        return { branch: br, n: l.length, toPick: l.filter((b) => ["open", "pending"].includes(bookingState(b))).length };
    });
    const list = branch ? dayList.filter((b) => branchOfBooking(b) === branch) : dayList;
    const live = list.filter((b) => bookingState(b) !== "cancelled");
    const shown = only ? list.filter((b) => bookingState(b) === only) : list;   // the table (the Cancelled card)
    // an Invygo booking ('invygo') is in none of the payment counts: it never pays Yelo (owner, 28 Sep 2026)
    const payCount = (st) => live.filter((b) => bookingPayment(b).state === st).length;
    const stateCount = (st) => list.filter((b) => bookingState(b) === st).length;

    // Per branch: what is booked (by car class, with the pick-up times) against the cars that
    // branch has for it now - Ready, or held for bookings (Maintenance status at an airport branch).
    const supply = useMemo(() => {
        const m = {};
        (fleetCars || []).forEach((c) => {
            const st = String(c.status || "").trim();
            if (!(/^ready$/i.test(st) || isHeld(c))) return;
            const br = branchOf({ "Pick-up Branch": c.branch });
            const k = `${br}|${String(c.category || "Unknown")}`;
            m[k] = m[k] || { ready: 0, held: 0 };
            if (/^ready$/i.test(st)) m[k].ready += 1; else m[k].held += 1;
        });
        return m;
    }, [fleetCars]);
    const byBranch = useMemo(() => {
        const out = {};
        live.forEach((b) => {
            const br = branchOf({ "Pick-up Branch": b.pickup_branch });
            const cls = bookingClass(b);
            out[br] = out[br] || {};
            const row = (out[br][cls] = out[br][cls] || { cls, total: 0, toPick: 0, times: [] });
            row.total += 1;
            if (["open", "pending"].includes(bookingState(b))) row.toPick += 1;
            row.times.push({ t: timeOf(b.pickup_date) || "—", state: bookingState(b) });
        });
        return BRANCHES.filter((br) => out[br]).map((br) => ({
            branch: br,
            rows: Object.values(out[br]).sort((a, b) => b.total - a.total).map((r) => ({ ...r, times: r.times.sort((x, y) => x.t.localeCompare(y.t)) })),
        }));
    }, [live]);

    const syncedAt = state.meta && state.meta.synced_at ? new Date(state.meta.synced_at) : null;
    const subtitle = syncedAt
        ? `From Ejar · updated ${syncedAt.toLocaleString("en-GB", { timeZone: "Asia/Dubai", hourCycle: "h23", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`
        : "From Ejar";

    if (!state.loading && !state.meta && !state.rows.length) {
        return (
            <Card className="mb-6" title="Bookings" subtitle="From Ejar, refreshed every hour">
                <EmptyState icon={ICONS.calendar} title="Bookings are not synced yet"
                    description={state.error && state.error.code !== "permission-denied"
                        ? `Could not read the bookings: ${String(state.error.message || state.error)}`
                        : "No bookings read from Ejar yet. The engine reads them every hour - if this stays empty, tell the admin."} />
            </Card>
        );
    }

    const heading = (isNew ? `New bookings · made ${dayWindowText(today, today)}, newest first`
        : `Bookings picking up · ${shortDay(picked, today)} · ${dayWindowText(picked, today).replace(/^Live · /, "")}`)
        + (branch ? ` · ${branch}` : "") + (only === "cancelled" ? " · cancelled only" : "");
    const dayChip = (d) => (
        <button key={d} type="button" onClick={() => setPicked(d)} aria-pressed={picked === d}
            className={`yelo-badge yelo-badge--${picked === d ? "accent" : "neutral"} ds-chip ${picked === d ? "is-active" : ""}`}>
            {shortDay(d, today)} <strong className="tabular-nums">{state.loading ? "…" : liveCount(d)}</strong>
        </button>
    );
    return (
        <Card className="mb-6" title="Bookings" subtitle={subtitle}
            actions={<div className="flex flex-wrap gap-2">
                <Button size="sm" leadingIcon={<Icon glyph={ICONS.search} size="sm" />} onClick={() => setChecking(true)}
                    title="A customer asks for a car: which of ours are free by a time, when the rented ones come back">Is a car free?</Button>
                {canAdd && <Button size="sm" variant="primary" leadingIcon={<Icon glyph={ICONS.plus} size="sm" />} onClick={() => setAdding(true)}
                    title="A booking that is not on Ejar: a customer on WhatsApp, the phone or at the counter">Add booking</Button>}
            </div>}>
            <AvailabilityDialog open={checking} onClose={() => setChecking(false)} fleetCars={fleetCars} bookings={state.rows} />
            <AddBookingDialog open={adding} today={today} onClose={() => setAdding(false)}
                onSaved={(doc) => { setAdding(false); setTick((t) => t + 1); toast.success(`Saved ${doc.booking_number} - sent to Telegram`); }} />
            <div ref={chipsRef} className="flex flex-wrap gap-2 mb-4" role="group" aria-label="Which bookings">
                <button type="button" onClick={() => setPicked("new")} aria-pressed={isNew}
                    title="Bookings made today (00:00:00 → now, Dubai), for any pick-up day"
                    className={`yelo-badge yelo-badge--${isNew ? "brand" : "neutral"} ds-chip ${isNew ? "is-active" : ""}`}>
                    New · Live <strong className="tabular-nums">{state.loading ? "…" : fresh.length}</strong>
                </button>
                {shownDays.map(dayChip)}
                {hiddenDays.length > 0 && (
                    <button type="button" onClick={() => setMoreDays((v) => !v)} aria-expanded={moreDays}
                        title={moreDays ? "Hide the other days" : "Show the other days with bookings"}
                        className="yelo-badge yelo-badge--neutral ds-chip">
                        {moreDays ? "− Less" : `+ ${hiddenDays.length} more day${hiddenDays.length > 1 ? "s" : ""}`}
                    </button>
                )}
            </div>

            <StatGrid min={150} className="mb-4">
                <Stat label="All branches" value={state.loading ? "…" : dayList.filter((b) => bookingState(b) !== "cancelled").length}
                    icon={ICONS.calendar} onClick={() => setBranch(null)} className={branch ? "" : "is-active"} />
                {branchCards.map((c) => (
                    <Stat key={c.branch} label={c.branch} value={state.loading ? "…" : c.n}
                        meta={c.toPick ? `${c.toPick} to pick up` : undefined}
                        onClick={() => setBranch((cur) => (cur === c.branch ? null : c.branch))}
                        className={branch === c.branch ? "is-active" : ""} title={`Show ${c.branch} only`} />
                ))}
            </StatGrid>

            <StatGrid min={150}>
                <Stat label={isNew ? "New bookings" : "Bookings"} value={state.loading ? "…" : live.length} icon={ICONS.calendar} />
                <Stat label="Paid" value={state.loading ? "…" : payCount("paid")} tone="success" />
                <Stat label="Not paid" value={state.loading ? "…" : payCount("due")} tone={payCount("due") ? "danger" : undefined} />
                <Stat label="Broker (invoice)" value={state.loading ? "…" : payCount("broker")} />
                <Stat label="Awaiting payment" value={state.loading ? "…" : payCount("awaiting")} tone={payCount("awaiting") ? "warning" : undefined} />
                {!isNew && <Stat label="Became contracts" value={state.loading ? "…" : stateCount("contract")} tone="success" />}
                {!isNew && <Stat label="No-show" value={state.loading ? "…" : stateCount("noshow")} tone={stateCount("noshow") ? "danger" : undefined} />}
                {/* the cancelled ones are in no other number (owner, 1 Oct 2026: "اللى اتكنسل يبقى ليه كارت يوضحه"): their
                    own card, which lists them */}
                <Stat label="Cancelled" value={state.loading ? "…" : stateCount("cancelled")}
                    meta={stateCount("cancelled") ? (only === "cancelled" ? "listed below · click to show all" : "click to list them") : undefined}
                    onClick={stateCount("cancelled") || only ? () => setOnly((o) => (o === "cancelled" ? null : "cancelled")) : undefined}
                    className={only === "cancelled" ? "is-active" : ""}
                    title="Bookings cancelled on Ejar - counted in no other card" />
            </StatGrid>

            {!isNew && branch && byBranch.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2" aria-label={`${branch}: cars for the bookings`}>
                    {byBranch[0].rows.map((r) => {
                        const have = supply[`${branch}|${r.cls}`] || { ready: 0, held: 0 };
                        const cars = have.ready + have.held;
                        return (
                            <span key={r.cls} className={`yelo-badge yelo-badge--${r.toPick > cars ? "danger" : "success"} ds-chip`} style={{ cursor: "default" }}
                                title={`${have.held} held for bookings · ${have.ready} ready at ${branch}`}>
                                {r.cls} · {r.toPick} to pick up · <strong className="tabular-nums">{cars}</strong> car{cars === 1 ? "" : "s"} there
                            </span>
                        );
                    })}
                </div>
            )}
            {!isNew && !branch && byBranch.length > 0 && (
                <div className="mt-4 grid gap-4 2xl:grid-cols-2">
                    {byBranch.map(({ branch, rows }) => (
                        <div key={branch}>
                            <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                {branch} · {rows.reduce((a, r) => a + r.total, 0)} bookings
                            </h4>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableHeader>Car class</TableHeader><TableHeader>Pick-up times</TableHeader>
                                            <TableHeader>To pick up</TableHeader><TableHeader>Cars there</TableHeader>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((r) => {
                                            const have = supply[`${branch}|${r.cls}`] || { ready: 0, held: 0 };
                                            const cars = have.ready + have.held;
                                            return (
                                                <TableRow key={r.cls}>
                                                    <TableCell><CellText>{r.cls}</CellText></TableCell>
                                                    <TableCell>
                                                        <span className="inline-flex flex-wrap justify-center gap-1">
                                                            {r.times.map((x, i) => (
                                                                <CellTag key={i} tone={x.state === "contract" ? "success" : x.state === "noshow" ? "danger" : "neutral"}>{x.t}</CellTag>
                                                            ))}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell><CellNumber value={r.toPick} /></TableCell>
                                                    <TableCell>
                                                        <span className="inline-flex flex-col items-center">
                                                            <CellNumber value={cars} tone={r.toPick > cars ? "danger" : "success"} />
                                                            <span className="ds-cell-muted">{have.held} held · {have.ready} ready</span>
                                                        </span>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    ))}
                </div>
            )}
            {!isNew && !branch && byBranch.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">Cars there = held for bookings (Maintenance status at that airport branch) + Ready at that branch; red when fewer than the bookings still to pick up. Green times = became a contract.</p>
            )}

            <div className="mt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">{heading} ({shown.length})</h4>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader>#</TableHeader><TableHeader>Booked at</TableHeader><TableHeader>Customer</TableHeader><TableHeader>Phone</TableHeader>
                                <TableHeader>Car type</TableHeader><TableHeader>Pick-up</TableHeader><TableHeader>Branch</TableHeader><TableHeader>Payment</TableHeader><TableHeader>Status</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shown.length === 0 ? (
                                <TableRow><TableCell colSpan={9} className="py-5 text-gray-500">{state.loading ? "Loading…" : "No bookings"}</TableCell></TableRow>
                            ) : shown.map((b, i) => {
                                const st = BOOKING_STATES.find((x) => x.key === bookingState(b));
                                const digits = phoneDigits(b.mobile);   // with the country code: a local 05... gets it
                                return (
                                    <TableRow key={b.id}>
                                        <TableCell><CellNumber value={i + 1} /></TableCell>
                                        <TableCell><WhenCell text={b.created_at} title={`${b.created_at || ""}${b.created_by ? ` · by ${b.created_by}` : ""}`} /></TableCell>
                                        <TableCell>
                                            <span className="inline-flex flex-col items-center gap-0.5">
                                                <span className="inline-block max-w-[150px] whitespace-normal leading-tight" title={`${b.customer} · booking ${b.booking_number}`}>{b.customer || "—"}</span>
                                                {b.manual && <span title={`Not on Ejar - added by ${b.created_by || "?"}${b.notes ? ` · ${b.notes}` : ""}`}><CellTag tone="warning">📱 {b.source || "Outside Ejar"}</CellTag></span>}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            {digits
                                                ? <a href={whatsappUrl(digits, bookingConfirmMessage(b))} target="_blank" rel="noopener noreferrer" className="ds-cell-phone hover:underline" title="Open in WhatsApp with the booking confirmation message">{formatPhone(digits)}</a>
                                                : <span className="ds-cell-empty">—</span>}
                                        </TableCell>
                                        <TableCell>
                                            <span className="inline-flex flex-col items-center leading-tight" title={b.vehicle}>
                                                <span className="max-w-[140px] whitespace-normal font-semibold">{bookingModel(b) || bookingClass(b)}</span>
                                                {bookingModel(b) && <span className="ds-cell-muted">{bookingClass(b)}</span>}
                                            </span>
                                        </TableCell>
                                        <TableCell><WhenCell text={b.pickup_date} /></TableCell>
                                        <TableCell><CellText title={b.pickup_branch}>{shortBranch(b.pickup_branch)}</CellText></TableCell>
                                        <TableCell><PaymentCell b={b} /></TableCell>
                                        <TableCell>
                                            <span className="inline-flex flex-col items-center gap-1">
                                                <CellTag tone={st ? st.tone : "neutral"}>{b.manual && st?.key === "contract" ? "Picked up" : (st ? st.label : b.status || "—")}</CellTag>
                                                {canUpdate && b.manual && bookingState(b) === "open" && (
                                                    <span className="inline-flex gap-1">
                                                        <Button size="xs" onClick={() => closeManual(b, "picked")}>Picked up</Button>
                                                        <Button size="xs" variant="ghost" onClick={() => closeManual(b, "cancelled")}>Cancel</Button>
                                                    </span>
                                                )}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </Card>
    );
};

/* ---------- Page ---------- */
const FRESH_MIN = 15;      // older saved copy -> read the database again on open
const ContractsStatistics = () => {
    const { currentUser } = useAuth();
    const { hasPermission, isAdmin } = usePermissions();
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [raw, setRaw] = useState(null);       // contracts rows as stored (for the cars summary)
    const [rows, setRows] = useState([]);       // Business Bay + Invygo, tagged
    const [cars, setCars] = useState([]);
    const [airport, setAirport] = useState({ opened: [], closed: [], loading: false });
    const [airportTick, setAirportTick] = useState(0);
    const [customerProfile, setCustomerProfile] = useState(null);
    const today = dubaiToday();
    const [mode, setMode] = useState("day");     // 'day' | 'range'
    // the report opens on the last WHOLE day - yesterday (owner, 5 Oct 2026: "دائما اعرض اليوم اللى خلص"); "Live" shows
    // today so far
    const yesterday = dayBefore(today);
    const [day, setDay] = useState(yesterday);
    // the day's report by e-mail (owner, 5 Oct 2026): what it looks like, and the admin's "Send it now"
    const [mail, setMail] = useState(null);               // { loading } | { data } | { error }
    const [mailSending, setMailSending] = useState(false);
    const previewMail = async () => {
        setMail({ loading: true });
        try { setMail({ data: await finesService.dailyEmailPreview(day) }); } catch (e) { setMail({ error: errorMessage(e) }); }
    };
    // who gets it is set in Administration > E-mail report (owner, 5 Oct 2026: "عايز كل ده يكون فى اعدادات الادمن")
    const sendMail = async () => {
        const data = mail?.data;
        if (!data || !(await confirmDialog({ title: "Send the daily report e-mail?",
            message: `${data.subject}\n\nTo: ${data.to.join(", ")}${(data.cc || []).length ? `\nCC: ${data.cc.join(", ")}` : ""}`,
            confirmLabel: "Send" }))) return;
        setMailSending(true);
        try {
            const out = await finesService.dailyEmailSend(data.day);
            toast.success(`Sent to ${out.to.join(", ")}${out.cc && out.cc.length ? ` (CC ${out.cc.join(", ")})` : ""}`);
        } catch (e) { toast.error(errorMessage(e)); } finally { setMailSending(false); }
    };
    const [from, setFrom] = useState(yesterday);
    const [to, setTo] = useState(yesterday);

    // The contracts store (USE_CONTRACTS_V2) serves the open contracts and the ones returned since a day, not every
    // contract: the Business Bay list starts at the first day shown (a past day's cars need that day's contracts),
    // and is read again, quietly, when an earlier day is picked. Firestore: every contract, as before.
    const sinceDay = USE_CONTRACTS_V2 ? (mode === "range" && from && to && from <= to ? from : day) || today : undefined;
    const loadedSince = useRef(sinceDay);
    const load = useCallback(async (force = false, since = loadedSince.current, quiet = force) => {
        if (quiet) setRefreshing(true); else setLoading(true);
        setError(null);
        try {
            const [bb, fleet] = await Promise.all([
                contractsService.getAllContracts(force, { since }),
                fleetService.getAllCars(force).catch(() => []),
            ]);
            loadedSince.current = since;
            setRaw(bb || []);
            setRows((bb || []).map(tag).filter((r) => !isAirportBranch(r.branch))); // airport comes per day
            setCars((fleet || []).filter((c) => c && isRtaRegistered(c) && !c.moved_to));
        } catch (e) { logger.error("Daily Rental Report load failed:", e); setError(e); }
        finally { setLoading(false); setRefreshing(false); }
    }, []);
    // Opening the page, no click needed: the contracts come fresh by themselves (getAllContracts reads
    // only what changed when its saved copy is older than 15 min); the fleet copy, when older than
    // FRESH_MIN minutes, is read again in the background (the Refresh button spins meanwhile).
    useEffect(() => {
        let alive = true;
        (async () => {
            await load(false);
            const fresh = await jsonCacheService.isCacheValid("fleet.json", FRESH_MIN / 60).catch(() => false);
            if (!alive || fresh) return;
            setRefreshing(true);
            try {
                const fleet = await fleetService.getAllCars(true);
                if (alive) setCars((fleet || []).filter((c) => c && isRtaRegistered(c) && !c.moved_to));
            } catch (e) { logger.warn("Fleet refresh failed:", e); }
            finally { if (alive) setRefreshing(false); }
        })();
        return () => { alive = false; };
    }, [load]);

    useEffect(() => {
        if (!USE_CONTRACTS_V2 || !sinceDay || !loadedSince.current || sinceDay >= loadedSince.current) return;
        load(false, sinceDay, true);
    }, [sinceDay, load]);

    const { usageOf, ready } = useFleetUsage(cars, { contracts: raw || undefined });

    // A past day: the nightly record of that day (fleet_snapshots/<day>), rented cars split by
    // the Business Bay contract that covered that day (airport branch -> Airport).
    const [snapshot, setSnapshot] = useState({ day: null, data: undefined });
    useEffect(() => {
        if (mode !== "day" || day === today) return undefined;
        let alive = true;
        setSnapshot({ day, data: undefined });
        fleetSnapshotService.get(day)
            .then((d) => { if (alive) setSnapshot({ day, data: d }); })
            .catch((e) => { logger.warn("Fleet snapshot read failed:", e); if (alive) setSnapshot({ day, data: null }); });
        return () => { alive = false; };
    }, [mode, day, today]);
    const plateKey = (p) => String(p || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    const contractsByPlate = useMemo(() => {
        const m = new Map();
        (raw || []).forEach((r) => {
            const k = plateKey(r["Plate No."] || r.plate_no);
            if (!k) return;
            if (!m.has(k)) m.set(k, []);
            m.get(k).push({ raw: r, pickIso: isoDay(g(r, "Pick-up Date", "pickup_date")), dropIso: isoDay(g(r, "Drop-off Date", "dropoff_date")) });
        });
        return m;
    }, [raw]);
    const contractsByNo = useMemo(() => new Map((raw || []).map((r) => [String(g(r, "Contract No.", "contract_no")).trim().toUpperCase(), r])), [raw]);
    const pastCars = useMemo(() => {
        const snap = snapshot.data;
        if (!snap || !Array.isArray(snap.cars)) return [];
        const current = new Map(cars.map((c) => [plateKey(c.plate_number), c]));
        // the night's in-fleet flag (engine FLT-24), else the same rule over the night's status
        return snap.cars.filter((c) => (typeof c.in_fleet === "boolean" ? c.in_fleet : inFleet({ rta_registered: c.rta, status: c.status }))).map((c) => ({ ...(current.get(plateKey(c.plate)) || {}), plate_number: c.plate, status: c.status, branch: c.branch,
            // the open contract that held the car that night (the engine's record since 18 Oct 2026)
            nightContract: c.contract || "", nightContractBranch: c.contract_branch || "" }));
    }, [snapshot.data, cars]);
    const pastUsage = useCallback((car) => {
        if (!car || !/rent/i.test(String(car.status || ""))) return null;
        // the night's own record says which contract held the car (an airport contract on a Business Bay car, a
        // replacement car): that contract's type, else its branch (owner, 18 Oct 2026: 3 cars still under "Other")
        const night = nightCategory(car, contractsByNo);
        if (night) return night;
        // older nights (no contract recorded): the contract whose own dates cover the day first; an open overdue one only when none does
        const mine = (contractsByPlate.get(plateKey(car.plate_number)) || []).filter((c) => contractCoversDay(c, day));
        const hit = mine.find((c) => !c.dropIso || c.dropIso >= day) || mine[0];
        if (hit) return contractCategory(hit.raw) || "Monthly";
        if (/airport|terminal/i.test(String(car.branch || ""))) return "Airport";
        return "Rented";
    }, [contractsByPlate, contractsByNo, day]);

    // The days shown (one, or the range - capped at 31 days)
    const days = useMemo(() => {
        if (mode === "day") return day ? [day] : [];
        if (!from || !to || from > to) return [];
        return daysBetween(from, to).slice(0, MAX_RANGE_DAYS);
    }, [mode, day, from, to]);
    const rangeTooLong = mode === "range" && from && to && from <= to && daysBetween(from, to).length > MAX_RANGE_DAYS;
    const daySet = useMemo(() => new Set(days), [days]);

    // Airport (T1/T2): exact queries for each day shown
    const airportKey = days.join(",");
    useEffect(() => {
        let alive = true;
        const list = airportKey ? airportKey.split(",") : [];
        if (!list.length) { setAirport({ opened: [], closed: [], loading: false }); return undefined; }
        setAirport((a) => ({ ...a, loading: true }));
        (async () => {
            // A failed read is shown as such, never as "0 opened / 0 closed" at T1 / T2 (CON-13)
            let results;
            try {
                // [[picked up that day, drop-off that day], ...] - two reads a day, or one for all (the store)
                results = await airportService.getAirportByDays(list.map(toDDMMYYYY));
            } catch (e) {
                logger.error("Daily Rental Report: airport contracts not read", e);
                if (alive) setAirport({ opened: [], closed: [], loading: false, error: e?.message || "read failed" });
                return;
            }
            if (!alive) return;
            const opened = []; const closed = [];
            results.forEach(([op, cl]) => {
                (op || []).map(tag).filter((r) => isAirportBranch(r.branch)).forEach((r) => opened.push(r));
                (cl || []).map(tag).filter((r) => isAirportBranch(r.branch) && r.closed).forEach((r) => closed.push(r));
            });
            setAirport({ opened, closed, loading: false });
        })();
        return () => { alive = false; };
    }, [airportKey, airportTick]);

    const byBranch = useCallback((list) => {
        const m = {}; BRANCHES.forEach((b) => (m[b] = []));
        list.forEach((r) => (m[r.branch] = m[r.branch] || []).push(r));
        return m;
    }, []);
    const openedByBranch = useMemo(() => {
        const m = byBranch([...rows.filter((r) => daySet.has(r.pickIso)), ...airport.opened]);
        Object.values(m).forEach((l) => l.sort((a, b) => (`${a.pickIso} ${a.pickTime}` < `${b.pickIso} ${b.pickTime}` ? 1 : -1)));
        return m;
    }, [rows, airport.opened, daySet, byBranch]);
    const closedByBranch = useMemo(() => {
        const m = byBranch([...rows.filter((r) => r.closed && daySet.has(r.dropIso)), ...airport.closed]);
        Object.values(m).forEach((l) => l.sort((a, b) => (`${a.dropIso} ${a.dropTime}` < `${b.dropIso} ${b.dropTime}` ? 1 : -1)));
        return m;
    }, [rows, airport.closed, daySet, byBranch]);

    const refresh = () => { setAirportTick((t) => t + 1); load(true); };

    const exportExcel = async () => {
        const XLSX = await import("xlsx");
        const opened = BRANCHES.flatMap((b) => openedByBranch[b] || []).map(exportRow);
        const closed = BRANCHES.flatMap((b) => closedByBranch[b] || []).map(exportRow);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, fitContractDateColumns(XLSX.utils.json_to_sheet(opened)), "Opened Contracts");
        XLSX.utils.book_append_sheet(wb, fitContractDateColumns(XLSX.utils.json_to_sheet(closed)), "Closed Contracts");
        const label = mode === "day" ? day : `${from}_to_${to}`;
        XLSX.writeFile(wb, `Contracts_Report_${label}.xlsx`);
        adminService.logUserAction("EXPORT_CONTRACTS", `Exported Daily Rental Report (${label})`, currentUser, { range: label });
    };

    if (loading) return <LoadingSpinner message="Loading contracts…" />;
    if (error) return (
        <EmptyState
            icon={ICONS.error}
            title="Could not load contracts"
            description={`Error: ${String(error.message || error)}`}
            action={<Button size="sm" onClick={() => load(true)} leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>Retry</Button>}
        />
    );

    const onCustomer = (r) => setCustomerProfile({ name: r.customer, contractNo: r.contract, plate: r.plate, model: r.model, branch: r.branch });
    const typeCount = (list, t) => list.filter((r) => r.type === t).length;
    const totalOpened = BRANCHES.reduce((s, b) => s + (openedByBranch[b] || []).length, 0);
    const totalClosed = BRANCHES.reduce((s, b) => s + (closedByBranch[b] || []).length, 0);
    const periodLabel = mode === "day" ? dayWindowText(day, today) : rangeWindowText(from, to, today);
    const isRange = mode === "range";
    // a number in the summary table scrolls to that branch's table below (and flashes it)
    const jumpTo = (branch, kind) => {
        const el = document.getElementById(jumpId(branch, kind));
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.remove("is-flash");
        void el.offsetWidth;                     // restart the flash
        el.classList.add("is-flash");
    };
    const JumpNumber = ({ value, tone, branch, kind, what }) => (value > 0
        ? <button type="button" className="ds-jump-link" onClick={() => jumpTo(branch, kind)} title={`Go to ${branch} · ${what}`}>
            <CellNumber value={value} tone={tone} />
        </button>
        : <CellNumber value={value} tone={tone} />);

    return (
        <div className="w-full">
            <div className="ds-toolbar mb-4 items-center">
                <Tabs
                    variant="segmented"
                    label="Report period"
                    items={[{ id: "day", label: "Day" }, { id: "range", label: "Date range" }]}
                    value={mode}
                    onChange={setMode}
                />
                {/* no labels above the fields: they would push the fields below the buttons */}
                {mode === "day" ? (
                    <Input type="date" size="sm" aria-label="Day" style={{ width: 150, flex: "none" }} value={day} max={today} onChange={(e) => setDay(e.target.value)} />
                ) : (
                    <>
                        <span className="text-sm text-gray-600">From</span>
                        <Input type="date" size="sm" aria-label="From" style={{ width: 150, flex: "none" }} value={from} max={today} onChange={(e) => setFrom(e.target.value)} />
                        <span className="text-sm text-gray-600">to</span>
                        <Input type="date" size="sm" aria-label="To" style={{ width: 150, flex: "none" }} value={to} max={today} onChange={(e) => setTo(e.target.value)} />
                    </>
                )}
                {mode === "day" && day !== today && <Button size="sm" variant="ghost" onClick={() => setDay(today)} title="Back to today, live">Live</Button>}
                <span className="text-xs text-gray-600 tabular-nums" title="A day is 24 hours, Dubai time">
                    {mode === "day" ? dayWindowText(day, today) : rangeWindowText(from, to, today)}
                </span>
                <Button size="sm" onClick={refresh} isLoading={refreshing} leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>Refresh</Button>
                <span className="ds-toolbar__spacer" />
                {hasPermission(PERMISSIONS.booking_export_results) && (
                    <Button size="sm" onClick={exportExcel} disabled={!days.length} leadingIcon={<Icon glyph={ICONS.export} size="sm" />}>Export Excel</Button>
                )}
                {mode === "day" && (
                    <Button size="sm" onClick={previewMail} title="This day's report e-mail, as it is sent every night">E-mail report</Button>
                )}
            </div>
            <Dialog open={!!mail} onClose={() => setMail(null)} size="xl" title={mail?.data ? mail.data.subject : "Daily report e-mail"}
                description={mail?.data ? (mail.data.ready
                    ? `Sent every night at ${mail.data.time} - yesterday's report`
                    : `Not sent yet: ${mail.data.missing}`) : undefined}
                footer={(
                    <>
                        <Button onClick={() => setMail(null)}>Close</Button>
                        {isAdmin && mail?.data && (
                            <Button variant="primary" onClick={sendMail} isLoading={mailSending} disabled={!mail.data.ready}>Send it now</Button>
                        )}
                    </>
                )}>
                {mail?.loading && <div className="py-10 flex justify-center"><LoadingSpinner /></div>}
                {mail?.error && <div role="alert" className="text-sm font-medium text-red-700">{mail.error}</div>}
                {mail?.data && (
                    <div className="grid gap-2 mb-3" style={{ gridTemplateColumns: "70px 1fr" }}>
                        <span className="text-sm font-semibold text-gray-700 self-center">From</span>
                        <span className="text-sm text-gray-900 self-center">{mail.data.from || "— (the e-mail account is not set on the server)"}</span>
                        <span className="text-sm font-semibold text-gray-700 self-center">To</span>
                        <span className="text-sm text-gray-900 self-center">{(mail.data.to || []).join(", ") || "—"}</span>
                        <span className="text-sm font-semibold text-gray-700 self-center">CC</span>
                        <span className="text-sm text-gray-900 self-center">{(mail.data.cc || []).join(", ") || "—"}</span>
                        <span />
                        <span className="text-xs text-gray-500">Who gets it is set in Administration &gt; E-mail report.</span>
                    </div>
                )}
                {mail?.data && (
                    <iframe title="The daily report e-mail" srcDoc={mail.data.html} sandbox=""
                        style={{ width: "100%", height: "70vh", border: "1px solid #e5e7eb", borderRadius: 8, background: "#f3f0fa" }} />
                )}
            </Dialog>
            {rangeTooLong && <p className="text-xs text-amber-700 mb-3">The range is limited to {MAX_RANGE_DAYS} days: showing {days[0]} → {days[days.length - 1]}.</p>}

            <SearchContracts
                rows={rows}
                canExport={hasPermission(PERMISSIONS.booking_export_results)}
                onCustomer={onCustomer}
            />

            {mode === "day" && (day === today ? (
                <CarsSummary cars={cars.filter(inService)} usageOf={usageOf} ready={ready} title="Cars — live now" subtitle="Ejar status + open contracts" />
            ) : snapshot.data === undefined ? (
                <CarsSummary title="Cars" empty={{ title: "Loading the record of this day…", description: "" }} />
            ) : snapshot.data ? (
                <CarsSummary cars={pastCars} usageOf={pastUsage} ready title={`Cars — ${day}`}
                    subtitle={`recorded ${String(snapshot.data.taken_at || "").slice(11, 16) || "23:59"} that night`} />
            ) : (
                <CarsSummary title="Cars" empty={{
                    title: "No record for this day",
                    description: "The engine records the fleet every night at 23:59. Days before that record started have no car summary.",
                }} />
            ))}

            {mode === "day" && <UpcomingBookings today={today} fleetCars={cars} reloadKey={airportTick} />}

            <Card
                className="mb-6"
                padding="none"
                title={`Contracts — ${periodLabel}`}
                subtitle="Click a number to jump to that branch"
                actions={(
                    <div className="flex items-center gap-2">
                        {airport.loading && <span className="text-xs text-gray-500">Loading airport…</span>}
                        {airport.error && (
                            <span className="text-xs font-semibold text-red-700" role="alert" title={airport.error}>
                                T1 / T2 not loaded - <button type="button" className="underline" onClick={() => setAirportTick((t) => t + 1)}>Retry</button>
                            </span>
                        )}
                        <Badge variant="accent">Opened: {totalOpened}</Badge>
                        <Badge variant="danger">Closed: {totalClosed}</Badge>
                    </div>
                )}
            >
                <div className="overflow-x-auto">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader>Branch</TableHeader>{TYPES.map((t) => <TableHeader key={t}>{t}</TableHeader>)}<TableHeader>Opened</TableHeader><TableHeader>Closed</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {BRANCHES.map((b) => {
                                const list = openedByBranch[b] || [];
                                return (
                                    <TableRow key={b}>
                                        <TableCell>
                                            <button type="button" className="ds-jump-link" onClick={() => jumpTo(b, "opened")} title={`Go to ${b}`}><CellText>{b}</CellText></button>
                                        </TableCell>
                                        {TYPES.map((t) => <TableCell key={t}><JumpNumber value={typeCount(list, t)} tone={categoryTone(t)} branch={b} kind="opened" what={`${t} opened`} /></TableCell>)}
                                        <TableCell><JumpNumber value={list.length} branch={b} kind="opened" what="opened" /></TableCell>
                                        <TableCell><JumpNumber value={(closedByBranch[b] || []).length} tone="danger" branch={b} kind="closed" what="closed" /></TableCell>
                                    </TableRow>
                                );
                            })}
                            <TableRow className="is-total">
                                <TableCell>TOTAL</TableCell>
                                {TYPES.map((t) => <TableCell key={t}><CellNumber value={BRANCHES.reduce((s, b) => s + typeCount(openedByBranch[b] || [], t), 0)} /></TableCell>)}
                                <TableCell><CellNumber value={totalOpened} /></TableCell>
                                <TableCell><CellNumber value={totalClosed} tone="danger" /></TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </Card>

            {BRANCHES.map((b) => (
                <div key={b} id={jumpId(b, "opened")} className="ds-jump-target mb-6">
                <Card
                    title={b}
                    actions={(
                        <div className="flex items-center gap-2">
                            <Badge variant="neutral">Opened: {(openedByBranch[b] || []).length}</Badge>
                            <Badge variant="neutral">Closed: {(closedByBranch[b] || []).length}</Badge>
                        </div>
                    )}
                >
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Opened {isRange ? "in this period" : `on ${day}`}</h4>
                    <ContractsTable rows={openedByBranch[b] || []} kind="opened" onCustomer={onCustomer} showDate={isRange} />
                    <h4 id={jumpId(b, "closed")} className="ds-jump-target text-sm font-semibold text-gray-900 mt-4 mb-2">Closed {isRange ? "in this period" : `on ${day}`}</h4>
                    <ContractsTable rows={closedByBranch[b] || []} kind="closed" onCustomer={onCustomer} showDate={isRange} />
                </Card>
                </div>
            ))}


            <p className="text-xs text-gray-500 mb-6">
                Airport branches are read exactly for each day shown; Business Bay &amp; Invygo come from the contracts
                collection (Refresh reads it fresh). &quot;Opened by&quot; is the Ejar user who created the contract
                (Business Bay contracts show it from their next import).
            </p>

            <CustomerProfileModal open={!!customerProfile} seed={customerProfile} onClose={() => setCustomerProfile(null)} onCopy={(m) => toast.success(m)} />
        </div>
    );
};

export default ContractsStatistics;
